// Accueil : trois cartes d'action (ma prochaine mission, reprendre mon entraînement, jouer
// directement un morceau), les récompenses, puis les réglages d'écoute et la progression.
// Les deux accès « Apprendre le piano » et « Jouer un morceau » restent visibles ; aucun
// cours n'est obligatoire pour ouvrir un morceau.

import { CATALOG, findScore } from '../../music/catalog';
import { levelOf } from '../../store/missions';
import { InputPicker } from '../components/InputPicker';
import { NextMissionCard, RewardsPanel } from '../components/Rewards';
import { formatDuration, formatMinutes, scoreDurationMs } from '../format';
import { useMissions, useProgress, useSettings } from '../hooks/stores';
import { INPUT_LABELS } from '../inputControl';
import { href } from '../router';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
}

export function HomePage() {
  const progress = useProgress();
  const settings = useSettings();
  const missions = useMissions();
  const last = progress.lastScoreId ? findScore(progress.lastScoreId) : undefined;
  const entries = Object.values(progress.scores);
  const passed = entries.filter((e) => e.status === 'passed').length;
  const started = entries.filter((e) => e.status === 'started').length;
  const exercises = CATALOG.filter((s) => s.kind === 'exercise');
  const pieces = CATALOG.filter((s) => s.kind === 'piece');
  const resume = last ?? exercises[0];
  const setupDone = settings.setupSource !== null && settings.setupDoneAt !== null;
  const onInstrument = settings.setupSource === 'mic' || settings.setupSource === 'midi';
  const lvl = levelOf(missions.xp);

  return (
    <main className="page home">
      <section className="hero">
        <p className="eyebrow">Apprendre le piano, sur son piano</p>
        <h1>Votre piano, une séance courte, un retour note par note.</h1>
        <p className="lede">Jouez, l’application écoute et vous dit ce qui est juste. Des missions courtes, des points et des badges pour avancer.</p>
        <div className="hero-stats" aria-label="Progression">
          <span className="level-pill">Niveau {lvl.level}</span>
          <span className="xp-pill">{missions.xp} XP</span>
          <span className="chip">
            {missions.awards.length} badge{missions.awards.length > 1 ? 's' : ''}
          </span>
        </div>
      </section>

      <section className="action-grid" aria-label="Pour commencer">
        <NextMissionCard state={missions} />

        <article className="action-card train">
          <p className="eyebrow">Apprendre le piano</p>
          <h2>Reprendre mon entraînement</h2>
          <p className="goal">
            {last ? `Dernier contenu : ${last.title}.` : `Premier exercice : ${resume.title}.`} {setupDone ? '' : 'Configurez d’abord votre piano : deux minutes.'}
          </p>
          <p className="setup-status">
            {setupDone && settings.setupSource ? (
              <>
                <span className={onInstrument && settings.setupVerdict === 'ok' ? 'badge passed' : 'badge started'}>
                  {onInstrument ? (settings.setupVerdict === 'ok' ? 'Piano configuré' : 'Piano à revoir') : settings.setupSource === 'keyboard' ? 'Clavier d’essai' : 'Synthèse (test)'}
                </span>
                <span>
                  {INPUT_LABELS[settings.setupSource]}, essai {settings.setupVerdict === 'ok' ? 'réussi' : 'incertain'} le {formatDate(settings.setupDoneAt ?? '')}
                  {onInstrument ? '.' : ' · le vrai piano reste à configurer.'}
                </span>
              </>
            ) : (
              <span className="muted">Micro ou câble MIDI, autorisation, court essai de notes.</span>
            )}
          </p>
          <div className="btn-row">
            <a className="btn primary" href={href('pratique', resume.id)}>
              {last ? 'Continuer' : 'Commencer'}
            </a>
            <a className="btn" href={href('configurer')}>
              {setupDone ? 'Mon piano' : 'Configurer mon piano'}
            </a>
            <a className="btn ghost" href={href('catalogue', 'exercices')}>
              Exercices
            </a>
          </div>
        </article>

        <article className="action-card play">
          <p className="eyebrow">Jouer un morceau</p>
          <h2>Jouer directement un morceau</h2>
          <p className="goal">Sans cours obligatoire : choisissez, les notes descendent, vous jouez.</p>
          <ul className="piece-list">
            {pieces.map((p) => (
              <li key={p.id}>
                <a href={href('pratique', p.id, 'notes')}>
                  <span>
                    {p.title}
                    {p.subtitle ? <span className="muted"> · {p.subtitle}</span> : null}
                  </span>
                  <span className="muted">
                    {formatDuration(scoreDurationMs(p))} · ♩ = {p.tempo}
                  </span>
                </a>
              </li>
            ))}
          </ul>
          <a className="btn coral" href={href('catalogue', 'morceaux')}>
            Tous les morceaux
          </a>
        </article>
      </section>

      <RewardsPanel state={missions} />

      <section className="panel">
        <h2>Comment l’application vous écoute</h2>
        <p className="muted">Micro : une note à la fois. MIDI : exact, accords compris. Clavier d’essai : sans piano. Rien n’est envoyé, tout est analysé sur votre appareil.</p>
        <details className="details">
          <summary>Changer la source d’écoute</summary>
          <InputPicker />
          <p className="muted">
            <a href={href('configurer')}>Configurer mon piano pas à pas</a> · <a href={href('reglages')}>Régler la latence</a> · <a href={href('aide')}>Brancher un piano, navigateurs compatibles</a>
          </p>
        </details>
      </section>

      {progress.totalSessions > 0 && (
        <section className="panel stats">
          <h2>Votre progression</h2>
          <div className="stat-row">
            <div>
              <span className="figure">{progress.totalSessions}</span>
              <span className="figure-label">séances</span>
            </div>
            <div>
              <span className="figure">{formatMinutes(progress.totalPracticeMs)}</span>
              <span className="figure-label">de pratique</span>
            </div>
            <div>
              <span className="figure">{passed}</span>
              <span className="figure-label">exercices réussis</span>
            </div>
            <div>
              <span className="figure">{started}</span>
              <span className="figure-label">en cours</span>
            </div>
          </div>
          <p className="muted">Enregistrée sur cet appareil, dans ce navigateur.</p>
        </section>
      )}
    </main>
  );
}
